import Vue from 'vue'
import App from './App.vue'
import router from './router'
import axios from 'axios'
import store from './store'
import jQuery from 'jquery'
import autoLogoutService from './utils/autoLogout'
import HolidayDealBadge from './components/common/HolidayDealBadge.vue'
import SummerSaleBadge from './components/common/SummerSaleBadge.vue'
import HugeSavingsBadge from './components/common/HugeSavingsBadge.vue'

Vue.config.productionTip = false

// Globally-registered sale badges (swap HolidayDealBadge -> HugeSavingsBadge when the holiday sale ends)
Vue.component('HolidayDealBadge', HolidayDealBadge)
Vue.component('SummerSaleBadge', SummerSaleBadge)
Vue.component('HugeSavingsBadge', HugeSavingsBadge)

// Global mixin for support phone number
Vue.mixin({
  computed: {
    supportPhoneRaw() {
      return process.env.VUE_APP_SUPPORT_PHONE || '';
    },
    supportPhone() {
      const p = this.supportPhoneRaw;
      if (p.length === 10) {
        return `(${p.slice(0, 3)}) ${p.slice(3, 6)}-${p.slice(6)}`;
      }
      return p;
    },
    supportPhoneTel() {
      return `tel:+1${this.supportPhoneRaw}`;
    }
  }
})

// Make axios available globally in Vue components as this.$axios
Vue.prototype.$axios = axios;

// Set up jQuery globally if you need it
window.jQuery = jQuery;
window.$ = jQuery;

// Set up the axios interceptor for requests
axios.interceptors.request.use(
  (config) => {
    let token = store.getters.authToken;
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Endpoints that represent a completed Stripe payment. A success on any of these
// means the user now has a card on file (a Stripe customer), so we flip is_stripe.
const STRIPE_PAYMENT_ENDPOINTS = [
  '/api/checkout-stripe',        // all GLP-1 / weight-loss card checkouts
  '/api/upgrade-plan',           // plan upgrades (guest + dashboard)
  '/api/process-subs-payment',   // billing "pay now" subscription charge
  '/api/confirm-klarna-payment', // every Klarna return (card-less, still a Stripe customer)
];

// Set up the axios interceptor for responses
axios.interceptors.response.use(
  (response) => {
    // Right after any successful NON-urgent-care payment, mark the user as a
    // Stripe (card-on-file) customer in-session so the dashboard/upgrade/billing
    // flows see is_stripe = true immediately — no logout→login needed.
    try {
      const url = response.config?.url || '';
      const isPaymentEndpoint = STRIPE_PAYMENT_ENDPOINTS.some((e) => url.includes(e));
      if (isPaymentEndpoint) {
        const data = response.data || {};
        const paymentSucceeded = data.status === 'success' || data.success === true;
        // Urgent care is the ONLY flow that must not flag the user as a Stripe
        // subscription customer; it tags its payload with medication: 'urgentcare'.
        let medication = '';
        try {
          medication = JSON.parse(response.config?.data || '{}').medication || '';
        } catch (e) { /* non-JSON body — treat as non-urgent-care */ }
        const isUrgentCare = medication === 'urgentcare';
        if (paymentSucceeded && !isUrgentCare && !store.getters.user?.is_stripe) {
          store.dispatch('UpdateUserData', { is_stripe: true });
        }
      }
    } catch (e) {
      // Never let this bookkeeping break the real response flow.
      console.error('is_stripe interceptor error:', e);
    }
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 403) {
      const role = error.response.data && error.response.data.role;
      if (role && role !== 'USER') {
        alert(`Access denied. Your account role (${role}) does not have permission to perform this action. Please log in with an authorized account.`);
      }
      return Promise.reject(error);
    }

    if (error.response && error.response.status === 401) {
      // Prevent redirect loop — don't redirect if already on /login
      const currentPath = router.currentRoute.path;
      if (currentPath === '/login') {
        return Promise.reject(error);
      }

      store.commit('updateAccessToken', null);
      const fullPath = router.currentRoute.fullPath;
      const query = { page_ref: fullPath };
      // Try to get email from the failed request body (e.g. intake form)
      try {
        const reqData = JSON.parse(error.config.data);
        if (reqData?.email) query.email = reqData.email;
      } catch (e) {console.log(e)}
      // Fall back to store user email (expired session)
      if (!query.email) {
        const user = store.getters.user;
        if (user?.email) query.email = user.email;
      }
      localStorage.removeItem('_ymain_key_');
      router.push({ path: '/login', query });
    }
    return Promise.reject(error);
  }
);

new Vue({
  el: '#app',
  router,
  store,
  render: h => h(App),
  created() {
    // Initialize auto-logout service
    autoLogoutService.init(store, router);
    
    // Watch for changes in authentication state and orders
    this.$store.watch(
      (state) => ({
        isAuthenticated: state.accessToken !== null,
        progressItems: state.progressItems
      }),
      () => {
        autoLogoutService.recheckUserStatus();
      },
      { deep: true }
    );
    
    // Watch for route changes to re-evaluate auto-logout status
    this.$router.afterEach(() => {
      autoLogoutService.recheckUserStatus();
    });
  }
});
